// Admin entity-management area (Step 5e Phase 7a). Admin-gated (RequireRole in App.tsx; the
// nav entry is hidden for non-admins). Tabs for Aircraft / Assets / Staff & Students / Bases,
// plus a link to the Orphaned-bookings page. Light theme (ops-board tokens) per the mockup.

import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { C, F, card, Btn, StatusPill } from '../components/admin/adminUi';
import AppHeader from '../components/AppHeader';
import FormModal from '../components/admin/FormModal';
import DeactivateDialog, { type Impact } from '../components/admin/DeactivateDialog';
import UserFormModal from '../components/admin/UserFormModal';
import BaseClosureModal from '../components/admin/BaseClosureModal';
import BookingKindsManager from '../components/admin/BookingKindsManager';
import TrainingActivitiesManager from '../components/admin/TrainingActivitiesManager';
import ReasonCodesManager from '../components/admin/ReasonCodesManager';
import TasksSettingsManager from '../components/admin/TasksSettingsManager';
import WhatsNewManager from '../components/admin/WhatsNewManager';
import CompanySettingsManager from '../components/admin/CompanySettingsManager';
import { aircraftFields, assetFields, baseFields, type AdminFieldDef } from '../components/admin/fields';
import type { FormValues } from '../components/admin/adminUi';
import * as api from '../lib/admin';
import { parseSessionTimes, formatSessionTimes } from '../lib/sessionTimes';

const TABS = [
  { key: 'aircraft', label: 'Aircraft' },
  { key: 'assets', label: 'Assets' },
  { key: 'users', label: 'Staff, Students & Clients' },
  { key: 'bases', label: 'Bases' },
  { key: 'booking-kinds', label: 'Booking types' },
  // 0.12.5: Ben, 2026-09-07 — "there is nowhere to amend what appears in the Activity list."
  // Sits next to Booking types because they are the two halves of the same choice on a booking:
  // the type is required, the activity qualifies it and is optional.
  { key: 'training-activities', label: 'Booking activities' },
  { key: 'reason-codes', label: 'Cancellation reasons' },
  { key: 'tasks', label: 'Task reminders' },
  { key: 'whats-new', label: "What's New" },
  { key: 'company', label: 'Company settings' },
] as const;

export default function Admin() {
  // Active section is URL-addressable (`/admin?section=users`) so the global ☰ menu can
  // deep-link straight to a tab and the choice is shareable. Default + unknown → aircraft.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawSection = searchParams.get('section');
  const tab = TABS.some((t) => t.key === rawSection) ? (rawSection as string) : 'aircraft';
  const setTab = (key: string) => setSearchParams(key === 'aircraft' ? {} : { section: key }, { replace: true });
  const [bases, setBases] = useState<api.AdminBase[]>([]);
  const [toast, setToast] = useState<string | null>(null);

  // 0.12.7 Part E: the admin-only full row — GET /api/bases is the cached reference read and omits contact fields.
  const loadBases = useCallback(() => { api.listBasesAdmin(true).then(setBases); }, []);
  useEffect(() => { loadBases(); }, [loadBases]);
  const baseOptions = bases.filter((b) => b.is_active).map((b) => ({ value: b.id, label: `${b.name} (${b.short_code})` }));

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: F }}>
      {/* Top-level nav (Orphaned bookings / Ops board) removed — both now live in the global
          ☰ menu (v0.3.1). Section navigation is the tabs below. */}
      <AppHeader context="Admin" />

      <nav style={{ display: 'flex', gap: 4, padding: '10px 18px 0', borderBottom: `1px solid ${C.border}`, overflowX: 'auto', flexWrap: 'nowrap', WebkitOverflowScrolling: 'touch' }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{ flexShrink: 0, whiteSpace: 'nowrap', border: `1px solid ${tab === t.key ? C.border : 'transparent'}`, borderBottom: 'none', background: tab === t.key ? C.surface : 'none', padding: '8px 16px', fontFamily: F, fontSize: 13, fontWeight: 600, color: tab === t.key ? C.accent : C.textDim, cursor: 'pointer', borderRadius: '7px 7px 0 0' }}>{t.label}</button>
        ))}
      </nav>

      <main style={{ padding: '20px 18px 60px', maxWidth: 1080, margin: '0 auto' }}>
        {toast && <div style={{ background: C.greenLight, border: `1px solid ${C.greenBorder}`, color: C.green, borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 13 }}>{toast}</div>}
        {tab === 'aircraft' && <ResourceManager config={aircraftConfig} onChanged={() => { setToast(null); }} setToast={setToast} />}
        {tab === 'assets' && <ResourceManager config={assetConfig(baseOptions)} onChanged={loadBases} setToast={setToast} />}
        {tab === 'bases' && <ResourceManager config={baseConfig} onChanged={loadBases} setToast={setToast} />}
        {tab === 'users' && <UsersManager baseOptions={baseOptions} setToast={setToast} />}
        {tab === 'booking-kinds' && <BookingKindsManager setToast={setToast} />}
        {tab === 'training-activities' && <TrainingActivitiesManager setToast={setToast} />}
        {tab === 'reason-codes' && <ReasonCodesManager setToast={setToast} />}
        {tab === 'tasks' && <TasksSettingsManager setToast={setToast} />}
        {tab === 'whats-new' && <WhatsNewManager setToast={setToast} />}
        {tab === 'company' && <CompanySettingsManager bases={bases} setToast={setToast} />}
      </main>
    </div>
  );
}

// ── Row layout ──────────────────────────────────────────────────────────────────
const rowStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderBottom: `1px solid ${C.border}` };
function Row({ title, subtitle, status, actions }: { title: ReactNode; subtitle?: ReactNode; status: ReactNode; actions: ReactNode }) {
  return (
    <div style={rowStyle}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{title} {status}</div>
        {subtitle && <div style={{ fontSize: 12, color: C.textDim, marginTop: 2 }}>{subtitle}</div>}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>{actions}</div>
    </div>
  );
}

// ── Generic resource manager (aircraft / assets / bases) ─────────────────────────
interface ResourceConfig<Row> {
  title: string;
  list: (includeInactive: boolean) => Promise<Row[]>;
  id: (r: Row) => string;
  name: (r: Row) => string;
  subtitle: (r: Row) => string;
  active: (r: Row) => boolean;
  deactReason: (r: Row) => string | null;
  fields: AdminFieldDef[];
  toForm: (r: Row) => FormValues;
  create: (payload: Record<string, unknown>) => Promise<api.Result<unknown>>;
  update: (id: string, payload: Record<string, unknown>) => Promise<api.Result<unknown>>;
  reactivate: (id: string) => Promise<api.Result<unknown>>;
  deactivate?: { reasons: readonly string[]; impact: (id: string) => Promise<Impact>; run: (id: string, body: api.DeactivateBody) => Promise<api.Result<{ orphaned: { count: number } }>> };
  closure?: boolean;
}

// Shared style for the per-list search box (matches the adminUi input tokens, a touch smaller).
const searchInp: CSSProperties = { fontFamily: F, fontSize: 12.5, padding: '5px 8px', border: `1px solid ${C.borderMed}`, borderRadius: 6 };

function ResourceManager<Row>({ config, onChanged, setToast }: { config: ResourceConfig<Row>; onChanged: () => void; setToast: (s: string | null) => void }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<{ kind: 'create' } | { kind: 'edit'; row: Row } | { kind: 'deact'; row: Row } | { kind: 'closure'; row: Row } | null>(null);

  const load = useCallback(() => { config.list(includeInactive).then(setRows); }, [config, includeInactive]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged(); };

  const createFields = config.fields;
  const editFields = config.fields.filter((f) => !f.createOnly);

  // Client-side filter over the already-loaded rows — matches the visible name + subtitle text.
  const q = search.trim().toLowerCase();
  const shown = q ? rows.filter((r) => `${config.name(r)} ${config.subtitle(r)}`.toLowerCase().includes(q)) : rows;

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <h1 style={{ fontSize: 17, margin: 0 }}>{config.title}</h1>
        <label style={{ marginLeft: 16, fontSize: 12.5, color: C.textDim, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} style={{ accentColor: C.accent }} /> include inactive
        </label>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" style={searchInp} />
          <Btn kind="primary" onClick={() => setModal({ kind: 'create' })}>+ New</Btn>
        </div>
      </div>

      {rows.length === 0 ? <div style={{ color: C.textMuted, fontStyle: 'italic', padding: 16 }}>None.</div>
        : shown.length === 0 ? <div style={{ color: C.textMuted, fontStyle: 'italic', padding: 16 }}>No matches.</div>
        : shown.map((r) => {
        const active = config.active(r);
        const reason = config.deactReason(r);
        return (
          <Row key={config.id(r)}
            title={config.name(r)}
            subtitle={<>{config.subtitle(r)}{!active && reason ? ` · deactivated: ${reason}` : ''}</>}
            status={<StatusPill active={active} />}
            actions={active ? <>
              <Btn kind="ghost" onClick={() => setModal({ kind: 'edit', row: r })}>Edit</Btn>
              {config.closure
                ? <Btn kind="danger" onClick={() => setModal({ kind: 'closure', row: r })}>Close base</Btn>
                : <Btn kind="danger" onClick={() => setModal({ kind: 'deact', row: r })}>Deactivate</Btn>}
            </> : <Btn kind="accent" onClick={async () => { const res = await config.reactivate(config.id(r)); if (!res.error) { setToast(`${config.name(r)} reactivated.`); refresh(); } }}>Reactivate</Btn>}
          />
        );
      })}

      {modal?.kind === 'create' && (
        <FormModal title={`New ${config.title.slice(0, -1)}`} fields={createFields} onSubmit={config.create} onClose={() => setModal(null)} onSaved={() => { setModal(null); setToast('Created.'); refresh(); }} />
      )}
      {modal?.kind === 'edit' && (
        <FormModal title={`Edit ${config.name(modal.row)}`} fields={editFields} initial={config.toForm(modal.row)} readOnlyHeader={`Editing ${config.name(modal.row)} — blank fields keep their current value.`}
          onSubmit={(p) => config.update(config.id(modal.row), p)} onClose={() => setModal(null)} onSaved={() => { setModal(null); setToast('Saved.'); refresh(); }} />
      )}
      {modal?.kind === 'deact' && config.deactivate && (
        <DeactivateDialog title={`Deactivate ${config.name(modal.row)}`} subject={config.name(modal.row)} reasons={config.deactivate.reasons}
          loadImpact={() => config.deactivate!.impact(config.id(modal.row))}
          onConfirm={(body) => config.deactivate!.run(config.id(modal.row), body)}
          onClose={() => setModal(null)} onDone={() => { setModal(null); setToast(`${config.name(modal.row)} deactivated.`); refresh(); }} />
      )}
      {modal?.kind === 'closure' && (
        <BaseClosureModal baseId={config.id(modal.row)} onClose={() => setModal(null)}
          onClosed={(summary) => { setModal(null); setToast(`Base closed — ${summary.bookings_orphaned} booking(s) moved to Orphaned.`); refresh(); }} />
      )}
    </div>
  );
}

// ── Configs ──────────────────────────────────────────────────────────────────
const dateOnly = (iso: string | null) => (iso ? iso.slice(0, 10) : undefined);

const aircraftConfig: ResourceConfig<api.AdminAircraft> = {
  title: 'Aircraft',
  list: api.listAircraft,
  id: (r) => r.registration,
  name: (r) => r.registration,
  subtitle: (r) => [r.model, r.aircraft_category, r.current_base_icao && `@ ${r.current_base_icao}`].filter(Boolean).join(' · '),
  active: (r) => r.is_active,
  deactReason: (r) => r.deactivation_reason,
  fields: aircraftFields(),
  toForm: (r) => ({
    model: r.model ?? undefined, aircraft_category: r.aircraft_category, base_icao: r.base_icao ?? undefined,
    current_base_icao: r.current_base_icao ?? undefined, availability_mode: r.availability_mode,
    approval_required: r.approval_required, approval_contact: r.approval_contact ?? undefined,
    capability_normal: r.capability_normal ?? undefined, capability_current: r.capability_current ?? undefined,
    serviceable: r.serviceable, status_note: r.status_note ?? undefined, expected_return_date: dateOnly(r.expected_return_date),
  }),
  create: api.createAircraft,
  update: api.updateAircraft,
  reactivate: api.reactivateAircraft,
  // Aircraft deactivate is step-up gated (destructive).
  deactivate: { reasons: api.AIRCRAFT_ASSET_REASONS, impact: (id) => api.aircraftImpact(id).then((count) => ({ count, requiresStepUp: true })), run: api.deactivateAircraft },
};

const assetConfig = (baseOptions: { value: string; label: string }[]): ResourceConfig<api.AdminAsset> => ({
  title: 'Assets',
  list: api.listAssets,
  id: (r) => r.id,
  name: (r) => r.name,
  subtitle: (r) => [r.category, r.ownership, baseOptions.find((b) => b.value === r.base_id)?.label].filter(Boolean).join(' · '),
  active: (r) => r.is_active,
  deactReason: (r) => r.deactivation_reason,
  fields: assetFields(baseOptions),
  toForm: (r) => ({
    name: r.name, short_code: r.short_code ?? undefined, category: r.category ?? undefined, ui_color: r.ui_color ?? undefined,
    base_id: r.base_id, capacity: r.capacity ?? undefined, ownership: r.ownership, availability_mode: r.availability_mode,
    approval_required: r.approval_required, require_booking_confirmation: r.require_booking_confirmation, timezone: r.timezone ?? undefined, ppr_required: r.ppr_required,
  }),
  create: api.createAsset,
  update: api.updateAsset,
  reactivate: api.reactivateAsset,
  // Assets are NOT in the step-up scope (base/aircraft/staff only) — plain confirm.
  deactivate: { reasons: api.AIRCRAFT_ASSET_REASONS, impact: (id) => api.assetImpact(id).then((count) => ({ count, requiresStepUp: false })), run: api.deactivateAsset },
});

// The session grid is the one base field the generic form cannot carry as-is: FormValues holds
// only string | number | boolean, and the column is a Json string[]. Converted at this seam —
// in one place, on the way out — rather than by widening the shared form machinery for a single
// field. An EMPTY box sends an explicit null, which is how the grid is cleared; omitting the key
// would leave it alone, which is not what a blanked box means.
function baseSessionPayload(payload: Record<string, unknown>): Record<string, unknown> {
  if (!('session_start_times' in payload)) return payload;
  const raw = payload.session_start_times;
  const times = parseSessionTimes(typeof raw === 'string' ? raw : '');
  return { ...payload, session_start_times: times.length ? times : null };
}

const baseConfig: ResourceConfig<api.AdminBase> = {
  title: 'Bases',
  // The Bases manager edits phone / email, so it reads the admin route that still returns them.
  list: api.listBasesAdmin,
  id: (r) => r.id,
  name: (r) => `${r.name} (${r.short_code})`,
  subtitle: (r) => [r.icao ?? 'no ICAO', r.timezone].filter(Boolean).join(' · '),
  active: (r) => r.is_active,
  deactReason: (r) => r.deactivation_reason,
  fields: baseFields(),
  toForm: (r) => ({
    name: r.name, short_code: r.short_code, ui_color: r.ui_color, icao: r.icao ?? undefined, timezone: r.timezone,
    operating_hours_start: r.operating_hours_start ?? undefined, operating_hours_end: r.operating_hours_end ?? undefined,
    // ⚠ THESE TWO WERE MISSING, so the edit form showed both boxes BLANK however a base's contact
    // details were recorded. Saving blank did not wipe them (buildPayload dropped the empty key),
    // so nothing was lost — but an admin could neither SEE the stored value nor CLEAR it. Both are
    // `clearable` now (fields.ts), so an emptied box sends '' and the schema reads that as null.
    phone: r.phone ?? undefined, email: r.email ?? undefined,
    // Prefilled so the edit form shows the current grid; blanking it clears the column.
    session_start_times: formatSessionTimes(r.session_start_times),
  }),
  create: (p) => api.createBase(baseSessionPayload(p)),
  update: (id, p) => api.updateBase(id, baseSessionPayload(p)),
  reactivate: api.reactivateBase,
  closure: true,
};

// ── Users manager (staff + students) ────────────────────────────────────────────
function UsersManager({ baseOptions, setToast }: { baseOptions: { value: string; label: string }[]; setToast: (s: string | null) => void }) {
  const [staff, setStaff] = useState<api.AdminStaff[]>([]);
  const [students, setStudents] = useState<api.AdminStudent[]>([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<{ kind: 'create' } | { kind: 'edit'; id: string } | { kind: 'deact'; id: string; name: string } | null>(null);

  const load = useCallback(() => { api.listStaff(includeInactive).then(setStaff); api.listStudents(includeInactive).then(setStudents); }, [includeInactive]);
  useEffect(() => { load(); }, [load]);

  const baseName = (id: string | null) => baseOptions.find((b) => b.value === id)?.label ?? '—';
  const raw = [
    ...staff.map((s) => ({ id: s.id, name: s.full_name, sub: `${s.role ?? 'staff'} · ${baseName(s.home_base_id)}`, active: s.is_active, reason: s.deactivation_reason })),
    // Phase 4: /api/students now returns students AND clients — label by `kind` (a client shows
    // its company, or its full_name when company-only), so clients aren't mislabeled as students.
    ...students.map((s) => ({
      id: s.id, name: s.full_name,
      sub: s.kind === 'client'
        ? `client · ${s.company || s.full_name} · ${baseName(s.home_base_id)}`
        : `student · ${s.training_stage ?? '—'} · ${baseName(s.home_base_id)}`,
      active: s.is_active, reason: s.deactivation_reason,
    })),
  ];
  // A user holding BOTH a staff role and a student/client role is returned by /api/staff AND
  // /api/students — and, transiently, while a role edit is mid-flight the two fetches can briefly
  // disagree. Collapse by id into ONE row (merging the role labels) so the list never renders two
  // rows with the same React key, which caused phantom/stale rows during deactivate/reactivate churn.
  const byId = new Map<string, { id: string; name: string; sub: string; active: boolean; reason: string | null }>();
  for (const u of raw) {
    const prev = byId.get(u.id);
    if (prev) prev.sub = `${prev.sub} · ${u.sub}`;
    else byId.set(u.id, { ...u });
  }
  const all = [...byId.values()];
  // Client-side filter — u.sub already carries role/base/company/training-stage, so all searchable.
  const q = search.trim().toLowerCase();
  const shown = q ? all.filter((u) => `${u.name} ${u.sub}`.toLowerCase().includes(q)) : all;

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <h1 style={{ fontSize: 17, margin: 0 }}>Staff, Students & Clients</h1>
        <label style={{ marginLeft: 16, fontSize: 12.5, color: C.textDim, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} style={{ accentColor: C.accent }} /> include inactive
        </label>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" style={searchInp} />
          <Btn kind="primary" onClick={() => setModal({ kind: 'create' })}>+ New</Btn>
        </div>
      </div>
      {all.length === 0 ? <div style={{ color: C.textMuted, fontStyle: 'italic', padding: 16 }}>None.</div>
        : shown.length === 0 ? <div style={{ color: C.textMuted, fontStyle: 'italic', padding: 16 }}>No matches.</div>
        : shown.map((u) => (
        <Row key={u.id} title={u.name} subtitle={<>{u.sub}{!u.active && u.reason ? ` · deactivated: ${u.reason}` : ''}</>} status={<StatusPill active={u.active} />}
          actions={u.active ? <>
            <Btn kind="ghost" onClick={() => setModal({ kind: 'edit', id: u.id })}>Edit</Btn>
            <Btn kind="danger" onClick={() => setModal({ kind: 'deact', id: u.id, name: u.name })}>Deactivate</Btn>
          </> : <Btn kind="accent" onClick={async () => { const r = await api.reactivateUser(u.id); if (!r.error) { setToast(`${u.name} reactivated.`); load(); } }}>Reactivate</Btn>} />
      ))}

      {modal?.kind === 'create' && <UserFormModal mode="create" baseOptions={baseOptions} onClose={() => setModal(null)} onSaved={() => { setModal(null); setToast('User created.'); load(); }} />}
      {modal?.kind === 'edit' && <UserFormModal mode="edit" userId={modal.id} baseOptions={baseOptions} onClose={() => setModal(null)} onSaved={() => { setModal(null); setToast('User saved.'); load(); }} />}
      {modal?.kind === 'deact' && (
        <DeactivateDialog title={`Deactivate ${modal.name}`} subject={modal.name} reasons={api.STAFF_REASONS}
          loadImpact={() => api.userDeactivatePreview(modal.id).then((p) => ({ count: p.count, requiresStepUp: p.requires_step_up }))}
          onConfirm={(body) => api.deactivateUser(modal.id, body)}
          onClose={() => setModal(null)} onDone={() => { setModal(null); setToast(`${modal.name} deactivated.`); load(); }} />
      )}
    </div>
  );
}
